import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { i18n } from '@wms/shared';
import { panelPosition, type PanelPlacement } from '../lib/hoverPreview.js';

const { t } = i18n;

const PANEL_WIDTH = 320;
/** Tall enough for ~6 rows; `panelPosition` caps it to what the viewport allows. */
const PANEL_MAX_HEIGHT = 260;
/** Grace period so the pointer can cross the gap from the number to the panel. */
const CLOSE_DELAY_MS = 150;

export interface ReservedByOrder {
  orderId: string;
  orderCode: string;
  status: string;
  projectRef: string | null;
  projectName: string | null;
  qty: number;
}

/**
 * The `Rezervuota` number with a hover (or tap) breakdown of which orders hold
 * the reservation — WMS-37. Jonas needs to see, straight from the stock list,
 * whose project a scarce component is spoken for.
 *
 * Renders a plain number when nothing is reserved, so the affordance only
 * appears where there's something to show.
 *
 * The panel is interactive, not a decorative tooltip: with many orders on one
 * component the list scrolls, so the pointer has to be able to enter it without
 * the panel vanishing. Closing is therefore deferred by `CLOSE_DELAY_MS` and
 * cancelled if the pointer lands on the panel; tapping outside, pressing
 * Escape, tabbing away or scrolling the page all close it.
 */
export function ReservedCell({
  value,
  rows,
  className = '',
}: {
  value: string;
  rows: ReservedByOrder[];
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<PanelPlacement | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Blur fires when a tap lands in the panel — don't close in that case. */
  const pointerInPanel = useRef(false);
  /**
   * True between a pointer press on the number and the click it produces.
   * Browsers focus a button during a tap, so the sequence is focus → click: if
   * focus opened the panel, the click behind it would immediately toggle it
   * shut and tapping would appear to do nothing. Only *keyboard* focus opens;
   * a pointer press leaves it to the click handler.
   */
  const pointerActivating = useRef(false);

  // Stable identities: these only touch refs and the state setter, so the
  // effect below can depend on `close` without re-subscribing every render.
  const cancelClose = useCallback(() => {
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);
  const close = useCallback(() => {
    cancelClose();
    pointerInPanel.current = false;
    setOpen(false);
  }, [cancelClose]);
  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  }, [cancelClose]);

  useEffect(() => cancelClose, [cancelClose]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: Event) => {
      const target = e.target as Node | null;
      if (btnRef.current?.contains(target ?? null)) return;
      if (panelRef.current?.contains(target ?? null)) return;
      close();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    // The panel is position-fixed against a coordinate taken once, so a page
    // scroll or resize would leave it stranded away from its number. Scrolling
    // *inside* the panel doesn't bubble to window, so the list still scrolls.
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', close);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', close);
      window.removeEventListener('resize', close);
    };
  }, [open, close]);

  if (rows.length === 0) return <span className={className}>{value}</span>;

  // Table cells live inside `overflow-x-auto` wrappers, which implicitly clip
  // overflow-y too — render the panel into a body-level portal (as ThumbButton
  // does) so it stays visible at the edges of the table.
  const place = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    setPos(
      panelPosition(
        r,
        { width: window.innerWidth, height: window.innerHeight },
        { width: PANEL_WIDTH, height: PANEL_MAX_HEIGHT },
      ),
    );
  };

  const openNow = () => {
    cancelClose();
    place();
    setOpen(true);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-expanded={open}
        aria-label={t('stock_dash.reserved_by.aria')}
        onPointerEnter={(e) => {
          if (e.pointerType === 'mouse') openNow();
        }}
        onPointerLeave={(e) => {
          if (e.pointerType === 'mouse') scheduleClose();
        }}
        onPointerDown={() => {
          pointerActivating.current = true;
        }}
        onFocus={() => {
          if (!pointerActivating.current) openNow();
        }}
        onBlur={() => {
          // A press that never became a click (dragged off) would otherwise
          // leave the flag set and swallow the next keyboard focus.
          pointerActivating.current = false;
          if (!pointerInPanel.current) close();
        }}
        onClick={() => {
          pointerActivating.current = false;
          if (open) close();
          else openNow();
        }}
        className={`cursor-help underline decoration-dotted underline-offset-2 ${className}`}
      >
        {value}
      </button>
      {open && pos
        ? createPortal(
            <div
              ref={panelRef}
              role="tooltip"
              onPointerEnter={() => {
                pointerInPanel.current = true;
                cancelClose();
              }}
              onPointerLeave={() => {
                pointerInPanel.current = false;
                scheduleClose();
              }}
              style={{
                position: 'fixed',
                top: pos.top,
                left: pos.left,
                width: pos.width,
                maxHeight: pos.height,
              }}
              className="z-50 flex flex-col rounded-md border border-border bg-background p-2 text-left shadow-lg"
            >
              <div className="mb-1 shrink-0 text-xs font-medium text-muted-foreground">
                {t('stock_dash.reserved_by.title')}
              </div>
              <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain">
                {rows.map((r) => (
                  <li key={r.orderId} className="flex items-baseline justify-between gap-2">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm">
                        {r.projectName ?? r.projectRef ?? t('stock_dash.reserved_by.no_project')}
                      </span>
                      <span className="block truncate font-mono text-[11px] text-muted-foreground">
                        {r.orderCode}
                      </span>
                    </span>
                    <span className="shrink-0 tabular-nums text-sm">{r.qty}</span>
                  </li>
                ))}
              </ul>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
